import { useMemo, useState } from "react";
import { Calendar, CheckCircle2, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useSafetyCheckins } from "@/lib/safety/useSafetyCheckins";

const GRACE_OPTIONS = [
  { value: "10", label: "10 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
];

const fmtLocal = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const SafetyCheckinPanel = () => {
  const { upcoming, past, loading, schedule, complete, cancel } = useSafetyCheckins();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const defaultLocal = useMemo(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);
  const [when, setWhen] = useState(defaultLocal);
  const [grace, setGrace] = useState("15");
  const [context, setContext] = useState("");

  const handleSchedule = async () => {
    const date = new Date(when);
    if (isNaN(date.getTime()) || date.getTime() < Date.now() + 60_000) {
      toast.error("Pick a time at least a minute from now.");
      return;
    }
    setBusy(true);
    const { error } = await schedule(date, parseInt(grace, 10), context.trim() || undefined);
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    setAdding(false);
    setContext("");
    toast.success("Check-in scheduled.");
  };

  const handleComplete = async (id: string) => {
    const { error } = await complete(id);
    if (error) toast.error(error);
    else toast.success("Marked safe ✓");
  };

  const handleCancel = async (id: string) => {
    if (!confirm("Cancel this check-in?")) return;
    const { error } = await cancel(id);
    if (error) toast.error(error);
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Calendar className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <p className="text-sm">
          Schedule a check-in before a date or activity. If you don't tap "I'm
          safe" within the grace window, your Trusted Circle gets a silent ping.
        </p>
      </div>

      {upcoming.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Upcoming
          </p>
          {upcoming.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{fmtLocal(c.scheduled_at)}</p>
                  <p className="text-xs text-muted-foreground">
                    Grace: {c.grace_minutes} min{c.context ? ` · ${c.context}` : ""}
                  </p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleCancel(c.id)}
                  aria-label="Cancel"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Button
                size="sm"
                className="w-full"
                onClick={() => handleComplete(c.id)}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                I'm safe
              </Button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="ci-when">When</Label>
              <Input
                id="ci-when"
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ci-grace">Grace window</Label>
              <Select value={grace} onValueChange={setGrace}>
                <SelectTrigger id="ci-grace">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRACE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="ci-ctx">Context (optional)</Label>
            <Textarea
              id="ci-ctx"
              maxLength={200}
              rows={2}
              placeholder="e.g. First date with @samira at Cafe Owl, 7pm"
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={handleSchedule} disabled={busy}>
              {busy ? "Scheduling…" : "Schedule"}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setAdding(true)}>
          <Clock className="h-4 w-4 mr-2" />
          Schedule a check-in
        </Button>
      )}

      {past.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Past check-ins ({past.length})
          </summary>
          <ul className="mt-2 space-y-1.5 text-xs">
            {past.slice(0, 10).map((c) => (
              <li key={c.id} className="flex items-center gap-2">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${
                  c.status === "completed" ? "bg-success"
                    : c.status === "missed" || c.status === "escalated" ? "bg-destructive"
                    : "bg-muted-foreground"
                }`} />
                <span className="text-muted-foreground">{fmtLocal(c.scheduled_at)}</span>
                <span className="capitalize">{c.status}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default SafetyCheckinPanel;
